
import { useEffect } from "react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/sections/Footer";
import { ArrowRight, CheckCircle, Calendar, Users, Building, Shield, Clock, Target, TrendingUp } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { caseStudies } from "@/data/caseStudies";
import { RedactedCompanyName } from "@/components/case-studies/RedactedCompanyName";
import { BreadcrumbNav } from "@/components/case-studies/BreadcrumbNav";

const OregonSkiResortSecuresSeasonal = () => {
  const caseStudy = caseStudies.find(study => study.id === "OregonSkiResortSecuresSeasonal");
  const companyName = caseStudy?.redactedName || "Cascade Mountain Resort";
  
  // Scroll to top on component mount
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero Section */}
        <section className="py-12 md:py-24 bg-gradient-to-br from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <BreadcrumbNav currentPage="Oregon Ski Resort Secures Seasonal Operations" />
            
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              <div className="lg:col-span-7">
                <div className="mb-6">
                  <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                    Case Study: Recreation & Tourism
                  </span>
                </div>
                <h1 className="text-4xl md:text-5xl font-bold text-gray-900 leading-tight mb-6">
                  How <RedactedCompanyName companyName={companyName} /> Secures Seasonal Operations
                </h1>
                <p className="text-xl text-gray-600 mb-8">
                  Learn how we helped an Oregon ski resort implement resilient security measures for seasonal operations, protect customer data, and maintain operational reliability in extreme conditions.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link to="/get-started">
                    <Button 
                      size="lg"
                      className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 shadow-md group"
                    >
                      Get Similar Results
                      <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                    </Button>
                  </Link>
                  <a href="#challenge">
                    <Button 
                      variant="outline"
                      size="lg"
                      className="border-purple-300 text-purple-800 hover:bg-purple-50"
                    >
                      Read the Case Study
                    </Button>
                  </a>
                </div>
              </div>
              <div className="lg:col-span-5">
                <Card className="bg-white border-0 shadow-xl rounded-xl overflow-hidden">
                  <div className="p-6 md:p-8 space-y-6">
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Industry</p>
                        <div className="flex items-center gap-2">
                          <Building className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Recreation & Tourism</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Company Size</p>
                        <div className="flex items-center gap-2">
                          <Users className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">200+ Employees</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Project Duration</p>
                        <div className="flex items-center gap-2">
                          <Calendar className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">4 Months</p>
                        </div>
                      </div>
                      <div className="space-y-1">
                        <p className="text-sm text-gray-500">Focus Areas</p>
                        <div className="flex items-center gap-2">
                          <Target className="h-4 w-4 text-purple-600" />
                          <p className="font-medium">Seasonal Security & Business Continuity</p>
                        </div>
                      </div>
                    </div>
                    
                    <div className="pt-4 border-t border-gray-100">
                      <h3 className="font-semibold text-gray-900 mb-3">Key Results:</h3>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">99.9% system uptime during peak season</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">75% reduction in security incidents</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <div className="h-5 w-5 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                            <CheckCircle className="h-3 w-3 text-green-600" />
                          </div>
                          <p className="text-gray-700">68% faster seasonal staff onboarding</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </Card>
              </div>
            </div>
          </div>
        </section>

        {/* Challenge Section */}
        <section id="challenge" className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-red-100 text-red-800 rounded-full text-sm font-medium mb-4">
                  The Challenge
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Unique Seasonal Security Challenges
                </h2>
                <p className="text-lg text-gray-600">
                  <RedactedCompanyName companyName={companyName} /> faced unique security and operational challenges due to its seasonal operations, remote mountain locations, and extreme weather conditions.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Seasonal Workforce</h3>
                  <p className="text-gray-600 mb-4">
                    Rapid onboarding and secure offboarding of 150+ seasonal employees each season created significant security management challenges.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Previous security breaches during staff transitions</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Weather Disruptions</h3>
                  <p className="text-gray-600 mb-4">
                    Frequent weather-related power and connectivity disruptions impacted IT operations, particularly during peak revenue periods.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Operational downtime during critical business periods</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Distributed Facilities</h3>
                  <p className="text-gray-600 mb-4">
                    Multiple locations across the mountain required secure connectivity and protection for both operational and customer data.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Inconsistent security across various facilities</p>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-red-50 to-white p-8 rounded-xl border border-red-100 shadow-sm">
                  <h3 className="text-xl font-semibold text-gray-900 mb-4">Customer Payment Security</h3>
                  <p className="text-gray-600 mb-4">
                    Need to protect sensitive customer payment data at all touchpoints while maintaining operational efficiency during peak periods.
                  </p>
                  <div className="flex items-start gap-2 text-red-700">
                    <div className="h-5 w-5 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Target className="h-3 w-3" />
                    </div>
                    <p className="text-sm font-medium">Previous data breach during holiday weekend</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-red-50 p-8 rounded-xl border border-red-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-red-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-red-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Breaking Point
                    </h3>
                    <p className="text-gray-700">
                      "Our business operates differently than most, with massive seasonal swings and challenging environments. Previous IT providers tried to apply standard corporate solutions that simply didn't work for our unique operational model."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— IT Director at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
        
        {/* Solution Section */}
        <section className="py-16 bg-gradient-to-br from-purple-50 to-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-purple-100 text-purple-800 rounded-full text-sm font-medium mb-4">
                  The Solution
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Customized Seasonal Security Solution
                </h2>
                <p className="text-lg text-gray-600">
                  Our approach focused on building a tailored Microsoft 365 security solution designed specifically for the resort's unique seasonal operations.
                </p>
              </div>
              
              <div className="space-y-8 mb-12">
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Users className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Seasonal Identity Management</h3>
                      <p className="text-gray-600 mb-4">
                        We implemented automated user lifecycle management for seasonal staff with role-based access controls and scheduled deprovisioning.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Automated onboarding and offboarding security workflows</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Role-based access control for staff position types</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Scheduled account deprovisioning at season end</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <Shield className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Multi-layered Data Protection</h3>
                      <p className="text-gray-600 mb-4">
                        We implemented comprehensive data protection for customer payment information across all resort touchpoints using Microsoft Purview and Information Protection.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">End-to-end encryption for payment data</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Data loss prevention policies for sensitive information</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">PCI DSS compliance controls across all touchpoints</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="bg-white p-8 rounded-xl border border-purple-100 shadow-sm">
                  <div className="flex items-start gap-4">
                    <div className="h-12 w-12 rounded-full bg-purple-100 flex items-center justify-center flex-shrink-0">
                      <TrendingUp className="h-6 w-6 text-purple-600" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-gray-900 mb-3">Weather-Resistant Infrastructure</h3>
                      <p className="text-gray-600 mb-4">
                        We deployed resilient communication systems and backup solutions specially designed to withstand extreme mountain weather conditions.
                      </p>
                      <div className="space-y-2">
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Redundant secure communication systems between facilities</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Power-independent failover capabilities for critical systems</p>
                        </div>
                        <div className="flex items-start gap-2">
                          <CheckCircle className="h-5 w-5 text-green-500 mt-0.5 flex-shrink-0" />
                          <p className="text-gray-700">Validated backup connectivity for emergency situations</p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="bg-purple-50 p-8 rounded-xl border border-purple-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-purple-200 flex items-center justify-center flex-shrink-0">
                    <Shield className="h-5 w-5 text-purple-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      The Transformation Moment
                    </h3>
                    <p className="text-gray-700">
                      "The moment we realized the transformation was during our busiest holiday weekend when we had a major power outage. In previous years, this would have meant total operational disruption. Instead, our systems continued running, transactions processed, and customers never noticed an issue."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— IT Director at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Results Section */}
        <section className="py-16 bg-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <div className="mb-12">
                <span className="inline-block px-4 py-1.5 bg-green-100 text-green-800 rounded-full text-sm font-medium mb-4">
                  The Results
                </span>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">
                  Measurable Security & Reliability Improvements
                </h2>
                <p className="text-lg text-gray-600">
                  Within four months, <RedactedCompanyName companyName={companyName} /> achieved transformative improvements in both security posture and operational reliability.
                </p>
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">99.9</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Uptime Achieved</h3>
                  <p className="text-gray-600">
                    Achieved near-perfect uptime for critical systems during peak season operations, ensuring consistent customer experience.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">75</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Security Incident Reduction</h3>
                  <p className="text-gray-600">
                    Dramatically decreased security incidents during peak operations through proactive monitoring and security management.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">68</span>
                    <span className="text-2xl font-medium text-green-600 ml-1">%</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Faster Staff Onboarding</h3>
                  <p className="text-gray-600">
                    Reduced seasonal staff onboarding time while simultaneously improving security through automated provisioning processes.
                  </p>
                </div>
                
                <div className="bg-gradient-to-br from-green-50 to-white p-8 rounded-xl border border-green-100 shadow-sm">
                  <div className="text-center mb-4">
                    <span className="text-4xl font-bold text-green-600">Zero</span>
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3 text-center">Data Incidents</h3>
                  <p className="text-gray-600">
                    No reported data security incidents since implementation, with full PCI DSS compliance validation across all payment touchpoints.
                  </p>
                </div>
              </div>
              
              <div className="bg-green-50 p-8 rounded-xl border border-green-100">
                <div className="flex items-start gap-4">
                  <div className="h-10 w-10 rounded-full bg-white border border-green-200 flex items-center justify-center flex-shrink-0">
                    <Clock className="h-5 w-5 text-green-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Long-Term Impact
                    </h3>
                    <p className="text-gray-700">
                      "Our business operates differently than most, with massive seasonal swings and challenging environments. edgefi understood these unique needs and built security that works with our business, not against it."
                    </p>
                    <p className="text-sm font-medium text-gray-700 mt-2">— IT Director at <RedactedCompanyName companyName={companyName} /></p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 bg-gradient-to-r from-purple-50 to-indigo-50">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-gray-900">
                Ready to Secure Your Seasonal Operations?
              </h2>
              <p className="text-xl text-gray-600 mb-8">
                Schedule a consultation to discover how we can help your organization achieve similar results with a customized Microsoft 365 security solution.
              </p>
              <div className="flex flex-wrap justify-center gap-6">
                <Link to="/get-started">
                  <Button 
                    size="lg"
                    className="bg-edgefi-purple text-white hover:bg-edgefi-purple/90 transition-colors px-8 py-6 h-auto shadow-lg hover:shadow-xl group"
                  >
                    Schedule Your Consultation
                    <ArrowRight className="ml-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </Button>
                </Link>
                <Link to="/services/microsoft365security">
                  <Button
                    variant="outline"
                    size="lg"
                    className="border-purple-300 text-purple-700 hover:bg-purple-50 px-8 py-6 h-auto"
                  >
                    Explore Microsoft 365 Security
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default OregonSkiResortSecuresSeasonal;
